import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  GitBranch,
  Play,
  Loader2,
  Download,
  Network,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransportIntelligence = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/transport-intelligence/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No transport analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/transport-intelligence/analyze`, {});
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running transport analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const getRAGColor = (rag) => {
    if (rag === 'GREEN') return 'bg-green-100 text-green-700 border-green-500';
    if (rag === 'AMBER') return 'bg-yellow-100 text-yellow-700 border-yellow-500';
    return 'bg-red-100 text-red-700 border-red-500';
  };

  const getRiskColor = (risk) => {
    if (risk === 'CRITICAL') return 'bg-red-600 text-white';
    if (risk === 'HIGH') return 'bg-orange-500 text-white';
    if (risk === 'MEDIUM') return 'bg-yellow-500 text-white';
    return 'bg-green-500 text-white';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Transport Intelligence Engine</h2>
            <p className="text-gray-600">Automated transport orchestration and conflict prediction</p>
            {analysis && analysis.sap_system_analyzed && analysis.sap_system_analyzed.name && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="text-gray-500">Analyzing System:</span>
                <span className="font-medium text-purple-700">{analysis.sap_system_analyzed.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  analysis.data_source === 'Real SAP System' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {analysis.data_source || 'Mock Data'}
                </span>
              </div>
            )}
          </div>
          <button 
            onClick={runAnalysis}
            disabled={analyzing}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Run Analysis</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <GitBranch className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Transport Analysis</h3>
          <p className="text-gray-600 mb-6">Analyze transport dependencies and predict conflicts</p>
          <button 
            onClick={runAnalysis}
            className="bg-purple-600 text-white px-8 py-3 rounded-xl hover:bg-purple-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Analysis</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <GitBranch className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_transports}</div>
              <div className="text-purple-100 text-sm">Total Transports</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertCircle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.collision_count}</div>
              <div className="text-orange-100 text-sm">Collisions</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Network className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.wave_count}</div>
              <div className="text-blue-100 text-sm">Waves</div>
            </div>
            <div className={`rounded-xl p-6 shadow-lg border-2 ${getRAGColor(analysis.summary.rag_status)}`}>
              <TrendingUp className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.readiness_score}</div>
              <div className="text-sm opacity-80">Readiness Score</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'collisions', label: 'Collisions', icon: AlertCircle },
                { id: 'waves', label: 'Transport Waves', icon: GitBranch },
                { id: 'cutover', label: 'Cutover Package', icon: Package },
                { id: 'narrative', label: 'AI Insights', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Transport Statistics</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Transports:</span>
                          <span className="font-bold">{analysis.summary.total_transports}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Dependency Edges:</span>
                          <span className="font-bold">{analysis.dependency_graph.edges.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Critical Path:</span>
                          <span className="font-bold">{analysis.dependency_graph.critical_path_length}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Collision Risk</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Collisions:</span>
                          <span className="font-bold">{analysis.summary.collision_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Critical:</span>
                          <span className="font-bold text-red-600">{analysis.summary.critical_collisions}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cyclic Deps:</span>
                          <span className={`font-bold ${analysis.dependency_graph.cyclic_dependencies_detected ? 'text-red-600' : 'text-green-600'}`}>
                            {analysis.dependency_graph.cyclic_dependencies_detected ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Cutover Package</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transports:</span>
                          <span className="font-bold">{analysis.summary.cutover_transports}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Import Time:</span>
                          <span className="font-bold">{analysis.cutover_package.estimated_import_time_hours}h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Collisions Tab */}
              {activeTab === 'collisions' && (
                <div className="space-y-4">
                  {analysis.collision_analysis.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                      <p>No transport collisions detected - Clean transport landscape!</p>
                    </div>
                  )}

                  {analysis.collision_analysis.map((collision) => (
                    <div key={collision.collision_id} className="bg-white border-2 border-orange-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getRiskColor(collision.risk_category)}`}>
                              {collision.risk_category}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{collision.collision_type}</h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">Object: {collision.object_name}</p>
                        </div>
                      </div>

                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-3">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Conflicting Transports:</p>
                        <div className="flex flex-wrap gap-2">
                          {collision.conflicting_transports.map((tr, idx) => (
                            <span key={idx} className="px-3 py-1 bg-white border border-orange-300 text-orange-700 text-xs font-mono rounded">
                              {tr}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Recommended Action:</p>
                        <p className="text-sm text-gray-700">{collision.recommended_action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Transport Waves Tab */}
              {activeTab === 'waves' && (
                <div className="space-y-4">
                  {analysis.transport_waves.map((wave) => (
                    <div key={wave.wave_id} className="bg-white border-2 border-purple-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{wave.wave_name}</h4>
                          <p className="text-sm text-gray-600">{wave.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-600">{wave.transport_count}</div>
                          <div className="text-xs text-gray-500">Transports</div>
                          <div className="text-xs text-gray-600 mt-1">{wave.estimated_duration_hours}h</div>
                        </div>
                      </div>

                      {wave.transport_ids.length > 0 && (
                        <div className="bg-purple-50 rounded-lg p-4 mb-3">
                          <p className="text-sm font-semibold text-gray-900 mb-2">Transports:</p>
                          <div className="flex flex-wrap gap-2">
                            {wave.transport_ids.map((tr, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white border border-purple-300 text-purple-700 text-xs font-mono rounded">
                                {tr}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Go/No-Go Criteria:</p>
                        <ul className="space-y-1">
                          {wave.go_no_go_criteria.map((criterion, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-center space-x-2">
                              <CheckCircle2 className="w-3 h-3 text-blue-600" />
                              <span>{criterion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {wave.dependencies.length > 0 && (
                        <div className="mt-3 text-xs text-gray-600">
                          <span className="font-semibold">Dependencies:</span> Wave {wave.dependencies.join(', Wave ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Cutover Package Tab */}
              {activeTab === 'cutover' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Production Cutover Transport Package</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Package ID</p>
                        <p className="font-bold text-gray-900">{analysis.cutover_package.package_id}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Total Transports</p>
                        <p className="text-3xl font-bold text-purple-600">{analysis.cutover_package.total_transports}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Import Time</p>
                        <p className="text-2xl font-bold text-blue-600">{analysis.cutover_package.estimated_import_time_hours}h</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Execution Sequence</h4>
                    <div className="space-y-2">
                      {analysis.cutover_package.execution_sequence.map((tr_id, idx) => (
                        <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </div>
                          <span className="font-mono text-sm font-semibold">{tr_id}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Pre-Import Checks</h4>
                    <ul className="space-y-2">
                      {analysis.cutover_package.pre_import_checks.map((check, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-blue-600" />
                          <span>{check}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* AI Narrative Tab */}
              {activeTab === 'narrative' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <Sparkles className="w-6 h-6 text-purple-600" />
                        <span>Transport Intelligence Summary</span>
                      </h3>
                      {analysis.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                          {analysis.ai_narratives.generated_by}
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{analysis.ai_narratives.executive_summary}</p>
                    </div>
                  </div>

                  {analysis.ai_narratives.merge_recommendations && analysis.ai_narratives.merge_recommendations.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Merge Recommendations</h4>
                      <div className="space-y-3">
                        {analysis.ai_narratives.merge_recommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-start space-x-3 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                            <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{rec.object}</p>
                              <p className="text-sm text-gray-700 mt-1">{rec.recommendation}</p>
                              <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-bold ${
                                rec.priority === 'High' ? 'bg-red-600 text-white' : 'bg-yellow-500 text-white'
                              }`}>
                                {rec.priority} Priority
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransportIntelligence;
